import { useState, useEffect, useRef } from "react";
import api from "../../../utils/api";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    OutlinedInput,
    FormHelperText,
    Button,
    Box,
    FormControl,
    FormLabel,
    CircularProgress,
    Avatar,
    IconButton,
    FormControlLabel,
    Switch,
    Typography,
    Select,
    MenuItem,
    InputLabel,
    InputAdornment,
    Alert,
    Divider,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import QuillEditor from "../../../components/QuillEditor";

const initial = {
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    position: "",
    bio: "",
    avatar: null,
    show_home: false,
    name_translations: { en: "", ru: "", hy: "" },
    position_translations: { en: "", ru: "", hy: "" },
    bio_translations: { en: "", ru: "", hy: "" },
};

const TRANSLATION_LOCALES = [
    { code: "en", label: "English" },
    { code: "ru", label: "Russian" },
    { code: "hy", label: "Armenian" },
];

function emptyRate() {
    return { lesson_type_id: "", hourly_rate: "" };
}

export default function TeacherModal({ open, onClose, row = null }) {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState(initial);
    const [imagePreview, setImagePreview] = useState(null);
    const [deleteAvatar, setDeleteAvatar] = useState(false);
    const [lessonTypes, setLessonTypes] = useState([]);
    const [rates, setRates] = useState([emptyRate()]);
    const [ratesError, setRatesError] = useState("");
    const bioEditorRef = useRef(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleTranslationChange = (field, locale, value) => {
        setForm((prev) => ({
            ...prev,
            [field]: { ...(prev[field] || {}), [locale]: value },
        }));
    };

    const buildTranslationsPayload = (field, { isHtml = false } = {}) => {
        const out = {};
        const t = form[field] || {};
        TRANSLATION_LOCALES.forEach(({ code }) => {
            const raw = t[code] || "";
            if (isHtml) {
                const plain = raw.replace(/<[^>]*>/g, "").trim();
                if (plain !== "") out[code] = raw;
            } else {
                const trimmed = raw.trim();
                if (trimmed !== "") out[code] = trimmed;
            }
        });
        return out;
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setForm((prev) => ({ ...prev, avatar: file }));
            setDeleteAvatar(false);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setForm((prev) => ({ ...prev, avatar: null }));
        setImagePreview(null);
        if (row?.avatar) setDeleteAvatar(true);
    };

    const getBio = () => {
        if (bioEditorRef.current && typeof bioEditorRef.current.getContent === "function") {
            return bioEditorRef.current.getContent() ?? form.bio;
        }
        return form.bio;
    };

    const updateRateRow = (index, field, value) => {
        setRates((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
    };

    const addRateRow = () => {
        setRates((prev) => [...prev, emptyRate()]);
    };

    const removeRateRow = (index) => {
        setRates((prev) => (prev.length <= 1 ? [emptyRate()] : prev.filter((_, i) => i !== index)));
    };

    const buildRatesPayload = () => rates
        .filter((r) => r.lesson_type_id !== "" && r.hourly_rate !== "")
        .map((r) => ({
            lesson_type_id: Number(r.lesson_type_id),
            hourly_rate: Number(r.hourly_rate),
            currency: "AMD",
        }));

    const handleSubmit = async () => {
        const ratesPayload = buildRatesPayload();

        setRatesError("");
        if (!ratesPayload.length) {
            setRatesError("Add at least one rate (lesson type + hourly rate).");
            return;
        }
        const seen = new Set();
        for (const r of ratesPayload) {
            if (seen.has(r.lesson_type_id)) {
                setRatesError("Each lesson type can only be set once.");
                return;
            }
            seen.add(r.lesson_type_id);
            if (!(r.hourly_rate >= 0)) {
                setRatesError("Hourly rate must be 0 or greater.");
                return;
            }
        }

        try {
            setSaving(true);
            setErrors({});

            const bio = getBio();
            const hasAvatarFile = form.avatar instanceof File;
            const isUpdate = !!row?.id;
            let teacherId = row?.id || null;

            const nameTranslations = buildTranslationsPayload("name_translations");
            const positionTranslations = buildTranslationsPayload("position_translations");
            const bioTranslations = buildTranslationsPayload("bio_translations", { isHtml: true });
            const appendTranslations = (formData) => {
                Object.entries(nameTranslations).forEach(([locale, value]) => {
                    formData.append(`name_translations[${locale}]`, value);
                });
                Object.entries(positionTranslations).forEach(([locale, value]) => {
                    formData.append(`position_translations[${locale}]`, value);
                });
                Object.entries(bioTranslations).forEach(([locale, value]) => {
                    formData.append(`bio_translations[${locale}]`, value);
                });
            };

            if (isUpdate && (hasAvatarFile || deleteAvatar)) {
                const formData = new FormData();
                formData.append('first_name', form.first_name);
                formData.append('last_name', form.last_name);
                formData.append('email', form.email);
                formData.append('position', form.position);
                formData.append('bio', bio);
                formData.append('show_home', form.show_home ? '1' : '0');
                if (form.password) formData.append('password', form.password);
                if (hasAvatarFile) formData.append('avatar', form.avatar);
                if (deleteAvatar) formData.append('delete_avatar', '1');
                appendTranslations(formData);
                await api.post(`admin/teachers/${row.id}/update`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else if (!isUpdate && hasAvatarFile) {
                const formData = new FormData();
                formData.append('first_name', form.first_name);
                formData.append('last_name', form.last_name);
                formData.append('email', form.email);
                formData.append('password', form.password);
                formData.append('position', form.position);
                formData.append('bio', bio);
                formData.append('show_home', form.show_home ? '1' : '0');
                formData.append('avatar', form.avatar);
                appendTranslations(formData);
                const res = await api.post("admin/teachers", formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                teacherId = res.data?.data?.id || null;
            } else if (!isUpdate) {
                const res = await api.post("admin/teachers", {
                    ...form,
                    bio,
                    name_translations: nameTranslations,
                    position_translations: positionTranslations,
                    bio_translations: bioTranslations,
                });
                teacherId = res.data?.data?.id || null;
            } else {
                const updateData = {
                    first_name: form.first_name,
                    last_name: form.last_name,
                    email: form.email,
                    position: form.position,
                    bio,
                    show_home: !!form.show_home,
                    name_translations: nameTranslations,
                    position_translations: positionTranslations,
                    bio_translations: bioTranslations,
                };
                if (form.password) updateData.password = form.password;
                await api.put(`admin/teachers/${row.id}`, updateData);
            }

            if (teacherId) {
                await api.put(`admin/teachers/${teacherId}/rates`, { rates: ratesPayload });
            }

            onClose(true);
        } catch (e) {
            const validationErrors = e.errors || e.response?.data?.errors;
            if (validationErrors?.rates?.[0]) {
                setRatesError(validationErrors.rates[0]);
            }
            if (validationErrors) {
                setErrors(validationErrors);
            } else if (e.response?.data?.message) {
                setRatesError(e.response.data.message);
            }
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const loadLessonTypes = async () => {
            try {
                const res = await api.get("admin/lesson-types/list");
                if (!cancelled) setLessonTypes(res.data?.data || []);
            } catch (err) {
                console.error("Failed to load lesson types", err);
            }
        };
        loadLessonTypes();
        return () => { cancelled = true; };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const loadRates = async () => {
            if (row?.id) {
                try {
                    const res = await api.get(`admin/teachers/${row.id}/rates`);
                    const list = (res.data?.data || []).map((r) => ({
                        lesson_type_id: String(r.lesson_type_id),
                        hourly_rate: String(r.hourly_rate ?? ""),
                    }));
                    if (!cancelled) setRates(list.length ? list : [emptyRate()]);
                } catch (err) {
                    if (!cancelled) setRates([emptyRate()]);
                }
            } else {
                if (!cancelled) setRates([emptyRate()]);
            }
        };
        loadRates();
        return () => { cancelled = true; };
    }, [open, row?.id]);

    useEffect(() => {
        if (row) {
            const incomingNameTranslations = (row.name_translations && typeof row.name_translations === "object")
                ? row.name_translations
                : {};
            const incomingPositionTranslations = (row.position_translations && typeof row.position_translations === "object")
                ? row.position_translations
                : {};
            const incomingBioTranslations = (row.bio_translations && typeof row.bio_translations === "object")
                ? row.bio_translations
                : {};
            setForm({
                first_name: row.first_name || "",
                last_name: row.last_name || "",
                email: row.email || "",
                password: "",
                position: row.position || "",
                bio: row.bio || "",
                avatar: null,
                show_home: Boolean(row.show_home),
                name_translations: {
                    en: incomingNameTranslations.en || "",
                    ru: incomingNameTranslations.ru || "",
                    hy: incomingNameTranslations.hy || "",
                },
                position_translations: {
                    en: incomingPositionTranslations.en || "",
                    ru: incomingPositionTranslations.ru || "",
                    hy: incomingPositionTranslations.hy || "",
                },
                bio_translations: {
                    en: incomingBioTranslations.en || "",
                    ru: incomingBioTranslations.ru || "",
                    hy: incomingBioTranslations.hy || "",
                },
            });
            if (row.avatar) {
                setImagePreview(row.avatar);
            } else {
                setImagePreview(null);
            }
            setDeleteAvatar(false);
        } else {
            setForm(initial);
            setImagePreview(null);
            setDeleteAvatar(false);
        }
        setErrors({});
        setRatesError("");
        setSaving(false);
    }, [row, open]);

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
            <DialogTitle>{!row ? "Create Teacher" : "Edit Teacher"}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <FormControl error={!!errors.avatar?.[0]}>
                        <FormLabel htmlFor="avatar">Thumbnail (optional)</FormLabel>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            {imagePreview && (
                                <Box sx={{ position: 'relative' }}>
                                    <Avatar
                                        src={imagePreview}
                                        sx={{ width: 80, height: 80 }}
                                    />
                                    <IconButton
                                        size="small"
                                        color="error"
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                                        }}
                                        onClick={handleRemoveImage}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                            <Button variant="outlined" component="label">
                                {imagePreview ? 'Change Image' : 'Upload Image'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                            </Button>
                        </Box>
                        {errors.avatar && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.avatar}>
                                {errors.avatar[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.first_name?.[0]}>
                        <FormLabel htmlFor="first_name">First Name</FormLabel>
                        <OutlinedInput
                            name="first_name"
                            fullWidth
                            value={form.first_name}
                            onChange={handleChange}
                        />
                        {errors.first_name && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.first_name}>
                                {errors.first_name[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.last_name?.[0]}>
                        <FormLabel htmlFor="last_name">Last Name</FormLabel>
                        <OutlinedInput
                            name="last_name"
                            fullWidth
                            value={form.last_name}
                            onChange={handleChange}
                        />
                        {errors.last_name && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.last_name}>
                                {errors.last_name[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Display name (translations)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Optional. When set, this name is shown publicly for the matching site language. Falls back to First + Last name when empty.
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
                            {TRANSLATION_LOCALES.map(({ code, label }) => {
                                const fieldErrorKey = `name_translations.${code}`;
                                const fieldError = errors[fieldErrorKey]?.[0];
                                return (
                                    <FormControl key={code} error={!!fieldError} fullWidth>
                                        <FormLabel htmlFor={`name_translations_${code}`}>{label}</FormLabel>
                                        <OutlinedInput
                                            id={`name_translations_${code}`}
                                            value={form.name_translations?.[code] ?? ""}
                                            onChange={(e) => handleTranslationChange("name_translations", code, e.target.value)}
                                            placeholder={`Display name in ${label.toLowerCase()}`}
                                        />
                                        {fieldError && (
                                            <FormHelperText sx={{ m: 0.5 }} error>{fieldError}</FormHelperText>
                                        )}
                                    </FormControl>
                                );
                            })}
                        </Box>
                    </Box>

                    <FormControl error={!!errors.email?.[0]}>
                        <FormLabel htmlFor="email">Email</FormLabel>
                        <OutlinedInput
                            name="email"
                            type="email"
                            fullWidth
                            value={form.email}
                            onChange={handleChange}
                        />
                        {errors.email && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.email}>
                                {errors.email[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl required error={!!errors.position?.[0]}>
                        <FormLabel htmlFor="position" required>Position / Title</FormLabel>
                        <OutlinedInput
                            name="position"
                            fullWidth
                            required
                            placeholder="e.g. English Language Teacher"
                            value={form.position}
                            onChange={handleChange}
                        />
                        {errors.position && (
                            <FormHelperText sx={{ m: 0.5 }} error>{errors.position[0]}</FormHelperText>
                        )}
                    </FormControl>

                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Position / Title (translations)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Optional. When set, this title is shown publicly for the matching site language. Falls back to the Position / Title field when empty.
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
                            {TRANSLATION_LOCALES.map(({ code, label }) => {
                                const fieldErrorKey = `position_translations.${code}`;
                                const fieldError = errors[fieldErrorKey]?.[0];
                                return (
                                    <FormControl key={code} error={!!fieldError} fullWidth>
                                        <FormLabel htmlFor={`position_translations_${code}`}>{label}</FormLabel>
                                        <OutlinedInput
                                            id={`position_translations_${code}`}
                                            value={form.position_translations?.[code] ?? ""}
                                            onChange={(e) => handleTranslationChange("position_translations", code, e.target.value)}
                                            placeholder={`Position in ${label.toLowerCase()}`}
                                        />
                                        {fieldError && (
                                            <FormHelperText sx={{ m: 0.5 }} error>{fieldError}</FormHelperText>
                                        )}
                                    </FormControl>
                                );
                            })}
                        </Box>
                    </Box>

                    <FormControl error={!!errors.show_home?.[0]}>
                        <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                                <Switch
                                    checked={Boolean(form.show_home)}
                                    onChange={(e) =>
                                        setForm((prev) => ({ ...prev, show_home: e.target.checked }))
                                    }
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2">Show on home page</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Display this teacher in the "Our Teachers" section on the home page.
                                    </Typography>
                                </Box>
                            }
                            labelPlacement="end"
                        />
                        {errors.show_home && (
                            <FormHelperText sx={{ m: 0.5 }} error>{errors.show_home[0]}</FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.bio?.[0]}>
                        <FormLabel sx={{ mb: 0.5 }}>Bio</FormLabel>
                        <QuillEditor
                            key={`bio-${row?.id ?? "new"}-${open}`}
                            ref={bioEditorRef}
                            value={form.bio || ""}
                            onChange={(html) => setForm((prev) => ({ ...prev, bio: html }))}
                            placeholder="Write a short bio about the teacher..."
                        />
                        {errors.bio && (
                            <FormHelperText sx={{ m: 0.5 }} error>{errors.bio[0]}</FormHelperText>
                        )}
                    </FormControl>

                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Bio (translations)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Optional. When set, this bio is shown publicly for the matching site language. Falls back to the Bio field above when empty.
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                            {TRANSLATION_LOCALES.map(({ code, label }) => {
                                const fieldErrorKey = `bio_translations.${code}`;
                                const fieldError = errors[fieldErrorKey]?.[0];
                                return (
                                    <FormControl key={code} error={!!fieldError} fullWidth>
                                        <FormLabel sx={{ mb: 0.5 }}>{label}</FormLabel>
                                        <QuillEditor
                                            key={`bio-${code}-${row?.id ?? "new"}-${open}`}
                                            value={form.bio_translations?.[code] || ""}
                                            onChange={(html) => handleTranslationChange("bio_translations", code, html)}
                                            placeholder={`Bio in ${label.toLowerCase()}`}
                                        />
                                        {fieldError && (
                                            <FormHelperText sx={{ m: 0.5 }} error>{fieldError}</FormHelperText>
                                        )}
                                    </FormControl>
                                );
                            })}
                        </Box>
                    </Box>

                    <FormControl error={!!errors.password?.[0]}>
                        <FormLabel htmlFor="password">
                            {row ? "New Password (leave blank to keep current)" : "Password"}
                        </FormLabel>
                        <OutlinedInput
                            name="password"
                            type="password"
                            fullWidth
                            value={form.password}
                            onChange={handleChange}
                        />
                        {errors.password && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.password}>
                                {errors.password[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <Divider sx={{ my: 1 }} />

                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Rates *
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Hourly rate per lesson type (in AMD). At least one rate is required.
                                </Typography>
                            </Box>
                            <Button size="small" startIcon={<AddIcon />} onClick={addRateRow}>
                                Add rate
                            </Button>
                        </Box>
                        {ratesError ? <Alert severity="error" sx={{ mb: 1 }}>{ratesError}</Alert> : null}
                        <Box display="flex" flexDirection="column" gap={1}>
                            {rates.map((rate, index) => (
                                <Box
                                    key={`rate-${index}`}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "1fr",
                                            sm: "minmax(220px, 1fr) 160px 80px 40px",
                                        },
                                        gap: 1,
                                        alignItems: "center",
                                    }}
                                >
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id={`rate-lesson-type-${index}`}>Lesson type</InputLabel>
                                        <Select
                                            labelId={`rate-lesson-type-${index}`}
                                            label="Lesson type"
                                            value={rate.lesson_type_id}
                                            onChange={(e) => updateRateRow(index, "lesson_type_id", e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>Select lesson type</em>
                                            </MenuItem>
                                            {lessonTypes.map((type) => (
                                                <MenuItem key={type.id} value={String(type.id)}>
                                                    {type.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <OutlinedInput
                                        size="small"
                                        type="number"
                                        value={rate.hourly_rate}
                                        onChange={(e) => updateRateRow(index, "hourly_rate", e.target.value)}
                                        inputProps={{ min: 0, step: 0.01 }}
                                        placeholder="Hourly rate"
                                        startAdornment={<InputAdornment position="start">֏</InputAdornment>}
                                    />
                                    <IconButton color="error" onClick={() => removeRateRow(index)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button
                    sx={{ color: '#fff' }}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving || (!row && !form.password)}
                >
                    {saving ? <CircularProgress size={24} /> : (!row ? "Create" : "Update")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
